import { useQuery } from '@tanstack/react-query';
import { KICODE } from 'queries/query-keys';
import KicodeService, {
  type LinkedSessionPage,
  type LinkedSessionQuery,
} from 'services/KicodeService';

export const blockResourcePath = (dashboardId: string, dashletId: string) =>
  `dashboard:${dashboardId}.dashlet:${dashletId}`;

export const codeStepResourcePath = (automationId: string, stepId: string) =>
  `agentic_workflow:${automationId}.code_step:${stepId}`;

/** `appId` is the BusinessPluginApp id; kicode records it lowercased. */
export const businessAppResourcePath = (appId: string) =>
  `business_plugin_app:${appId.toLowerCase()}`;

export const businessAppResumeQuery = (
  appId: string | null | undefined,
  employeeId: string | null | undefined
): LinkedSessionQuery | null =>
  appId && employeeId
    ? {
        resourcePath: businessAppResourcePath(appId),
        builder: 'business_app',
        createdBy: employeeId,
      }
    : null;

export const linkedSessionId = (
  page: LinkedSessionPage | null | undefined
): string | null => {
  const sessionId = page?.results?.[0]?.session_id;
  return typeof sessionId === 'string' && sessionId ? sessionId : null;
};

export type ResumeLookup =
  | { pending: true }
  | { pending: false; conversationId: string | null };

export interface ResumeQueryState {
  status: 'pending' | 'success' | 'error';
  data?: string | null;
}

/** A linked session wins; an empty or failed lookup falls back. */
export const settleResumeLookup = (
  { status, data }: ResumeQueryState,
  fallback: string | null
): ResumeLookup =>
  status === 'pending'
    ? { pending: true }
    : {
        pending: false,
        conversationId: (status === 'success' && data) || fallback,
      };

// The chat reads the resolved id once at mount, so a lookup never refetches
// (no focus/reconnect/remount refresh, errored queries included) and never outlives the panel: every open asks
// webapp again, and closing mid-lookup aborts it.
export const RESUME_LOOKUP_OPTIONS = {
  retry: false,
  throwOnError: false,
  staleTime: Infinity,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
  retryOnMount: false,
} as const;

export const linkedSessionsQueryOptions = (
  query: LinkedSessionQuery | null
) => ({
  queryKey: KICODE.LINKED_SESSIONS(
    query?.resourcePath,
    query?.builder,
    query?.createdBy
  ),
  queryFn: async ({ signal }: { signal: AbortSignal }) =>
    query
      ? linkedSessionId(
          await KicodeService.getLinkedSessions(query, { signal })
        )
      : null,
  enabled: query !== null,
  ...RESUME_LOOKUP_OPTIONS,
});

/**
 * The conversation to resume: the linked session, else `fallback`. Without a
 * query there is nothing to look up and `fallback` decides now.
 */
export const useResumeConversationId = (
  query: LinkedSessionQuery | null,
  fallback: string | null
): ResumeLookup => {
  const { status, data } = useQuery(linkedSessionsQueryOptions(query));
  return query
    ? settleResumeLookup({ status, data }, fallback)
    : { pending: false, conversationId: fallback };
};
